import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';

export interface UsernameValidationResult {
  isChecking: boolean;
  isUnique: boolean | null;
  error: string | null;
  checkUsername: (username: string, currentUsername?: string) => Promise<boolean>;
}

export function useUsernameValidation(): UsernameValidationResult {
  const [isChecking, setIsChecking] = useState(false);
  const [isUnique, setIsUnique] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { currentCommunity } = useCommunity();

  const checkUsername = useCallback(async (username: string, currentUsername?: string): Promise<boolean> => {
    if (!currentCommunity?.id) {
      setError('Community not found');
      return false;
    }

    // If username hasn't changed, it's valid
    if (currentUsername && username === currentUsername) {
      return true;
    }

    try {
      const { data, error } = await supabase.rpc('check_username_unique_in_community', {
        _community_id: currentCommunity.id,
        _username: username
      });

      if (error) {
        console.error('Error checking username uniqueness:', error);
        setError('Error checking username availability');
        return false;
      }

      return data;
    } catch (err) {
      console.error('Username check failed:', err);
      setError('Username check failed');
      return false;
    }
  }, [currentCommunity?.id]);

  const debouncedCheck = useCallback((username: string, currentUsername?: string) => {
    if (!username || username.length < 3) {
      setIsChecking(false);
      setIsUnique(null);
      setError(null);
      return;
    }

    setIsChecking(true);
    setError(null);
    setIsUnique(null);

    const timer = setTimeout(async () => {
      const unique = await checkUsername(username, currentUsername);
      setIsUnique(unique);
      if (!unique) {
        setError('This username is taken in your community.');
      }
      setIsChecking(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [checkUsername]);

  return {
    isChecking,
    isUnique,
    error,
    checkUsername: debouncedCheck
  };
}